import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { useFormField } from '../FormField';
import './Input.css';

/**
 * Textarea — Input 의 시각 규격을 그대로 따르는 여러 줄 입력 (02-components.md B3).
 * 최소 높이는 `--h-lg × 2`, 세로로만 늘린다. 내용에 맞춰 «자라는» 것이 필요하면
 * 기존 `AutoGrowTextarea` 를 쓴다 — 그쪽은 높이를 스스로 정하므로 resize 를 끈다.
 */
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  fullWidth?: boolean;
  /** 세로 크기 조절 손잡이를 없앤다. */
  fixedSize?: boolean;
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, fullWidth = false, fixedSize = false, wrapperClassName, className, id, disabled, required, readOnly,
    'aria-describedby': ariaDescribedBy, ...rest },
  ref,
) {
  const field = useFormField();
  const controlId = id ?? field?.controlId;
  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled ?? false;
  const isRequired = required ?? field?.required ?? false;
  const describedBy = [ariaDescribedBy, field?.describedBy].filter(Boolean).join(' ') || undefined;

  const wrapCls = [
    'wc-textarea',
    isInvalid ? 'wc-textarea--invalid' : '',
    isDisabled ? 'wc-textarea--disabled' : '',
    readOnly ? 'wc-textarea--readonly' : '',
    fullWidth ? 'wc-textarea--full' : '',
    fixedSize ? 'wc-textarea--fixed' : '',
    wrapperClassName ?? '',
  ].filter(Boolean).join(' ');

  return (
    <span className={wrapCls}>
      <textarea
        ref={ref}
        id={controlId}
        className={['wc-textarea__el', 'scrollbar-thin', className ?? ''].filter(Boolean).join(' ')}
        disabled={isDisabled}
        readOnly={readOnly}
        required={isRequired || undefined}
        aria-required={isRequired || undefined}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        {...rest}
      />
    </span>
  );
});
